import type { ManagerRow, ManagerScope, StaffDashboard } from '@fruuit/shared'
import {
  Badge,
  Callout,
  Card,
  DataTable,
  LoadingRegion,
  PageHeader,
  SkeletonText,
  StatGroup,
  StatTile,
  StatusBadge,
  statusLabel,
} from '@fruuit/ui'
import { useState } from 'react'
import { Link } from 'react-router'
import { errorMessage } from '../../api/client'
import { dateTimeIn } from '../../features/calendar/time'
import { formatDate, formatDateTime, plural } from '../../features/coverage/format'
import { useStaffDashboard } from '../../features/dashboard/api'
import { usePreferences, useSavePreferences } from '../../features/preferences/api'
import { useTitle } from '../../lib/useTitle'

const KIND_LABEL: Record<ManagerRow['kind'], string> = {
  case: 'Case',
  objective: 'Objective',
  milestone: 'Evidence review',
  dialogue: 'Correspondence',
  meeting: 'Meeting',
  questions: 'Questions',
  requests: 'Requests',
}

/** S1 team variant: the engagement-manager view and the staff work queue (I-10, GAP-UI-023). */
export function DashboardPage() {
  useTitle('Dashboard')
  // The toggle is remembered per user on the server (I-06); until it loads, nothing is fetched.
  const preferences = usePreferences()
  const { save } = useSavePreferences()
  const [chosen, setChosen] = useState<ManagerScope | null>(null)
  const scope: ManagerScope =
    chosen ?? preferences.data?.preferences.staff_dashboard_scope ?? 'mine'
  const dashboard = useStaffDashboard(scope, { enabled: !preferences.isPending })
  const setScope = (value: ManagerScope) => {
    setChosen(value)
    if (preferences.data && preferences.data.preferences.staff_dashboard_scope !== value) {
      void save({ staff_dashboard_scope: value })
    }
  }
  return (
    <>
      <PageHeader
        eyebrow="Fruuit workspace"
        title="Today's work, in one place."
        description="What is overdue, what is coming up and what is waiting on someone else."
        actions={
          <div className="inline-flex rounded-md border border-line bg-surface p-0.5">
            {(['mine', 'all'] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={scope === value}
                onClick={() => setScope(value)}
                className={
                  scope === value
                    ? 'rounded px-3 py-1.5 text-sm font-semibold text-white bg-teal'
                    : 'rounded px-3 py-1.5 text-sm text-teal hover:bg-wash'
                }
              >
                {value === 'mine' ? 'My cases' : 'All managers'}
              </button>
            ))}
          </div>
        }
      />
      {dashboard.error ? (
        <Callout tone="danger" role="alert" title="The dashboard could not be loaded">
          {errorMessage(dashboard.error)}
        </Callout>
      ) : dashboard.data ? (
        <Body d={dashboard.data} />
      ) : (
        <LoadingRegion label="Loading dashboard">
          <SkeletonText lines={10} />
        </LoadingRegion>
      )}
    </>
  )
}

function Body({ d }: { d: StaffDashboard }) {
  const m = d.manager
  const all = m.scope === 'all'
  return (
    <div className="space-y-6">
      <StatGroup>
        <StatTile
          label="Overdue actions"
          value={m.overdue.length}
          note="Past their due date"
          href="#overdue"
        />
        <StatTile
          label="Meetings, next 14 days"
          value={m.upcoming_meetings.length}
          note="Published and draft"
          href="#meetings"
        />
        <StatTile
          label="Outstanding responses"
          value={m.outstanding.length}
          note="Correspondence, questions and requests"
          href="#outstanding"
        />
        <StatTile
          label="Drafts awaiting review"
          value={m.drafts_awaiting_review}
          note={`${plural(d.queue.my_reviews, 'item')} for you to review`}
          href="/workspace/review"
        />
      </StatGroup>
      <div className="grid gap-6 xl:grid-cols-2">
        <RowsCard
          id="overdue"
          eyebrow="Act now"
          title="Overdue actions"
          rows={m.overdue}
          all={all}
          empty="Nothing is overdue."
        />
        <RowsCard
          id="meetings"
          eyebrow="Next 14 days"
          title="Upcoming meetings"
          rows={m.upcoming_meetings}
          all={all}
          empty="No meetings in the next two weeks."
          dateTime
        />
        <RowsCard
          id="outstanding"
          eyebrow="Waiting on others"
          title="Outstanding responses"
          rows={m.outstanding}
          all={all}
          empty="No company replies, investor questions or requests are waiting."
        />
        <RowsCard
          id="deadlines"
          eyebrow="Next 30 days"
          title="Deadlines"
          rows={m.deadlines}
          all={all}
          empty="No deadlines in the next 30 days."
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card
          eyebrow="Review & release"
          title="Waiting for your review"
          actions={
            <Link to="/workspace/review" className="font-semibold text-teal hover:underline">
              Open the queue →
            </Link>
          }
        >
          <p className="mb-3 text-sm text-sage">
            {d.queue.awaiting_review} in review · {d.queue.awaiting_publication} approved and
            awaiting publication · {plural(d.queue.my_drafts, 'draft')} of yours in progress
          </p>
          {d.review_items.length === 0 ? (
            <p className="text-sm text-sage">Nothing is waiting for your independent review.</p>
          ) : (
            <ul className="divide-y divide-line text-sm">
              {d.review_items.map((i) => (
                <li
                  key={`${i.kind}-${i.id}`}
                  className="flex items-start justify-between gap-3 py-2.5 first:pt-0"
                >
                  <span className="min-w-0">
                    <span className="block text-xs text-sage">
                      {statusLabel(i.kind)} · {i.company ?? 'Company'}
                    </span>
                    <span className="block font-medium text-ink">{i.title}</span>
                  </span>
                  <StatusBadge status={i.state} />
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card eyebrow="Across clients" title="Work queue">
          <dl className="grid grid-cols-2 gap-4 text-sm">
            {[
              ['Open investor questions', d.queue.open_questions, '/workspace/inbox'],
              ['Open participation requests', d.queue.open_requests, '/workspace/inbox'],
              ['Requests assigned to you', d.queue.requests_assigned_to_me, '/workspace/inbox'],
              ['Mapping exceptions', d.queue.mapping_exceptions, '/workspace/coverage'],
              ['Reviews overdue', d.queue.review_overdue, '/workspace/coverage'],
              ['New feed signals', d.queue.new_signals, '/workspace/signals'],
            ].map(([label, value, href]) => (
              <div key={label as string}>
                <dt className="text-xs text-sage">{label}</dt>
                <dd>
                  <Link
                    to={href as string}
                    className="font-display text-2xl font-semibold text-teal hover:underline"
                  >
                    {value}
                  </Link>
                </dd>
              </div>
            ))}
          </dl>
        </Card>
      </div>

      <Card eyebrow="Clients" title="Open work by client">
        <DataTable
          caption="Open work by client"
          rows={d.clients}
          getRowId={(r) => r.org_id}
          empty={<p className="p-4 text-sm text-sage">No client organisations yet.</p>}
          columns={[
            {
              id: 'name',
              header: 'Client',
              sortValue: (r) => r.name,
              cell: (r) => (
                <Link
                  to={`/workspace/coverage?org=${r.org_id}`}
                  className="font-semibold text-teal hover:underline"
                >
                  {r.name}
                </Link>
              ),
            },
            {
              id: 'holdings',
              header: 'Holdings',
              align: 'right',
              sortValue: (r) => r.holdings,
              cell: (r) => r.holdings,
            },
            {
              id: 'exceptions',
              header: 'Mapping exceptions',
              align: 'right',
              sortValue: (r) => r.open_exceptions,
              cell: (r) =>
                r.open_exceptions ? <Badge tone="watch">{r.open_exceptions}</Badge> : 0,
            },
            {
              id: 'overdue',
              header: 'Reviews overdue',
              align: 'right',
              sortValue: (r) => r.overdue_reviews,
              cell: (r) =>
                r.overdue_reviews ? <Badge tone="attention">{r.overdue_reviews}</Badge> : 0,
            },
            {
              id: 'questions',
              header: 'Questions',
              align: 'right',
              sortValue: (r) => r.open_questions,
              cell: (r) => r.open_questions,
            },
            {
              id: 'requests',
              header: 'Requests',
              align: 'right',
              sortValue: (r) => r.open_requests,
              cell: (r) => r.open_requests,
            },
          ]}
        />
      </Card>
    </div>
  )
}

function RowsCard({
  id,
  eyebrow,
  title,
  rows,
  all,
  empty,
  dateTime = false,
}: {
  id: string
  eyebrow: string
  title: string
  rows: ManagerRow[]
  all: boolean
  empty: string
  dateTime?: boolean
}) {
  return (
    <Card id={id} eyebrow={eyebrow} title={`${title} (${rows.length})`}>
      {rows.length === 0 ? (
        <p className="text-sm text-sage">{empty}</p>
      ) : (
        <ul className="divide-y divide-line">
          {rows.slice(0, 12).map((r) => (
            <li key={`${r.kind}-${r.id}`} className="py-3 first:pt-0 last:pb-0">
              <Link
                to={
                  r.meeting_id
                    ? `/workspace/meetings/${r.meeting_id}`
                    : `/workspace/cases/${r.engagement_id}`
                }
                className="flex items-start justify-between gap-3 rounded-sm hover:bg-wash"
              >
                <span className="min-w-0 text-sm">
                  <span className="block text-xs text-sage">
                    {KIND_LABEL[r.kind]} · {r.company}
                    {all && r.manager_name ? ` · ${r.manager_name}` : ''}
                  </span>
                  <span className="block font-medium text-ink">{r.title}</span>
                  {r.detail ? <span className="block text-xs text-sage">{r.detail}</span> : null}
                </span>
                {r.date ? (
                  <span className="shrink-0 text-right text-xs text-sage">
                    {dateTime
                      ? r.timezone
                        ? dateTimeIn(r.date, r.timezone)
                        : formatDateTime(r.date)
                      : formatDate(r.date)}
                  </span>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      )}
      {rows.length > 12 ? (
        <p className="mt-3 text-xs text-sage">And {rows.length - 12} more.</p>
      ) : null}
    </Card>
  )
}
